import { firebaseFunctions, fst as firestore } from "../../../global/firebase";
import {
  OrganizationDoc,
  WorkoutLog,
  Group,
  GroupMember,
  UserMonthlyStatsDoc,
  InviteDocument,
  TemporaryPasswordInviteDocument,
  StepLog,
  StatWorkoutLog,
  StatLearningLibraryLog,
  LearningLibraryLog,
} from "@sworkit/api";
import { format } from "date-fns";

import { getOrgInvites } from "../../../global/firestore-invites";
import { getLoggedInUserId } from "../../../global/firestore-user";
import {
  arrayRemove,
  arrayUnion,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  DocumentData,
  DocumentReference,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  QuerySnapshot,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { removeUndefinedAndNaNRecursive } from "../../../global/utils";
import { activityTypesByKey } from "../../../global/activity-types";
import portalGroupsState from "../../../stencil-store/portal-org-groups.store";
// import type firebaseType from 'firebase';

export async function getAllOrgs() {
  const orgsCollection = collection(firestore, `/organizations`);
  const orgsQuery = query(orgsCollection);
  const querySnapshot = await getDocs(orgsQuery);
  const orgs = querySnapshot.docs.map((doc) => doc.data() as OrganizationDoc);
  return orgs;
}

export async function getAdminOrgs() {
  const uid = getLoggedInUserId();
  if (!uid) return [];
  const adminDoc = await getDoc(doc(firestore, `/orgAdmins/${uid}`));
  const { orgIds } = adminDoc?.data() || {};
  if (!orgIds || !orgIds?.length) {
    return [];
  }
  const queries = await Promise.all(
    orgIds?.map((orgId) => getDoc(doc(firestore, `/organizations/${orgId}`))),
  );
  const orgs = queries.map((doc) => {
    return { ...doc.data(), id: doc.id } as OrganizationDoc;
  });
  return orgs;
}

export async function getOrgsFromList(adminUids: string[]) {
  if (!adminUids) return [];

  const adminDocs = await Promise.all(
    adminUids.map((uid) => getDoc(doc(firestore, `/orgAdmins/${uid}`))),
  );
  const allOrgIds = [
    ...new Set(
      adminDocs
        ?.map((orgAdminDoc) => orgAdminDoc?.data()?.["orgIds"] as string[])
        ?.flat() || [],
    ),
  ]; //todo typescript type on GroupMember
  if (!allOrgIds || !allOrgIds?.length) {
    return [];
  }
  const queries = await Promise.all(
    allOrgIds?.map((orgId) =>
      getDoc(doc(firestore, `/organizations/${orgId}`)),
    ),
  );
  const orgs = queries.map((doc) => {
    return { ...doc.data(), id: doc.id } as OrganizationDoc;
  });
  return orgs;
}

export async function getOrgMembers(orgId: string) {
  const query = await getDocs(
    collection(firestore, `organizations/${orgId}/members`),
  );
  const members = query.docs.map(
    (doc) =>
      ({ ...(doc.data() as GroupMember), uid: doc.id, orgId }) as GroupMember,
  );
  return members;
}

export async function getAssignedMembers(assignmentId: string) {
  const query = await getDocs(
    collection(firestore, `assignments/${assignmentId}/assignmentMembers`),
  );
  const members = query.docs.map((doc) => doc.data() as AssignedMember);
  return members;
}

export async function getAssignment(assignmentId: string) {
  const assignment = await getDoc(
    doc(firestore, `assignments/${assignmentId}`),
  );
  return assignment.data() as Assignment;
}

export async function getOrgAdmins(org: OrganizationDoc) {
  const editors = org.editors || [];
  const admins = [...editors, org.owner];
  const promises = admins
    .filter((uid) => (uid ? true : false))
    .map(async (uid) => getOrgMember(org.id, uid));
  const members = await Promise.all(promises);
  // remove undefined
  return members.filter((member) => !!member);
}

export async function getOrgMember(orgId: string, uid: string) {
  const orgMemberDoc = await getDoc(
    doc(firestore, `/organizations/${orgId}/members/${uid}`),
  );
  const groupMember = orgMemberDoc.data() as GroupMember;
  return groupMember;
}

async function getOrgWorkoutDataBetweenTimestamps(
  orgId: string,
  fromTimestamp: number,
  toTimestamp: number,
) {
  const fromYearMonth = format(fromTimestamp, "yyyy-MM");
  const toYearMonth = format(toTimestamp, "yyyy-MM");
  const q = await getDocs(
    query(
      collectionGroup(firestore, "stats"),
      where("orgId", "==", orgId),
      where("monthYear", ">=", fromYearMonth),
      where("monthYear", "<=", toYearMonth),
      orderBy("monthYear", "desc"),
    ),
  );
  return statsQueryToStatsByUserId(q, fromTimestamp, toTimestamp);
}

export function getOrgWorkoutData(orgId: string, dateRange: SwDateRange) {
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) return;
  const fromTimestamp = dateRange.startDate.getTime();
  const toTimestamp = dateRange.endDate.getTime();
  return getOrgWorkoutDataBetweenTimestamps(orgId, fromTimestamp, toTimestamp);
}

export async function getGroupWorkoutData(
  groupId: string,
  dateRange: SwDateRange,
) {
  const fromTimestamp = dateRange.startDate.getTime();
  const toTimestamp = dateRange.endDate.getTime();
  const fromYearMonth = format(fromTimestamp, "yyyy-MM");
  const toYearMonth = format(toTimestamp, "yyyy-MM");
  const q = await getDocs(
    query(
      collectionGroup(firestore, "stats"),
      where("groups", "array-contains", groupId),
      where("monthYear", ">=", fromYearMonth),
      where("monthYear", "<=", toYearMonth),
      orderBy("monthYear", "desc"),
    ),
  );
  return statsQueryToStatsByUserId(q, fromTimestamp, toTimestamp);
}

function statsQueryToStatsByUserId(
  query: QuerySnapshot<DocumentData>,
  fromTimestamp: number,
  toTimestamp: number,
) {
  const dataByUserId: StatsByUserId = {};

  query.forEach((doc) => {
    const uid = doc.ref.parent.parent.id;
    const statDoc = doc.data() as UserMonthlyStatsDoc;
    if (!statDoc) return;
    let previousStats;
    if (dataByUserId[uid]) {
      previousStats = { ...dataByUserId[uid] };
    }
    const workoutLogs = Object.values(statDoc?.workoutLogs || {});
    const stepLogs = Object.entries(statDoc?.stepLogs || {}).map(
      ([stepDay, stepLog]) => ({ ...stepLog, stepDay }),
    ) as StepLog[];
    const learningLibraryLogs = Object.values(
      statDoc?.learningLibraryLogs || {},
    );
    dataByUserId[uid] = totalFromStatDoc(
      workoutLogs,
      stepLogs,
      learningLibraryLogs,
      fromTimestamp,
      toTimestamp,
      previousStats,
    );
  });
  return dataByUserId;
}

export function totalFromStatDoc(
  workoutLogs: StatWorkoutLog[],
  stepLogs: StepLog[],
  learningLibraryLogs: StatLearningLibraryLog[],
  fromTimestamp?,
  toTimestamp?,
  previousStats?: GroupMemberStats,
) {
  const memberStats = previousStats || getDefaultMemberStats();
  for (const log of workoutLogs) {
    if (
      fromTimestamp &&
      toTimestamp &&
      (log.timestampMillis < fromTimestamp || log.timestampMillis > toTimestamp)
    ) {
      continue;
    }
    const dateString = log.createdOn.substring(0, 10);
    if (!memberStats.workoutsByDate[dateString]) {
      memberStats.workoutsByDate[dateString] = {
        activeMinutes: 0,
        workoutCount: 0,
        meditationCount: 0,
        assessmentCount: 0,
      };
      memberStats.activeDaysCount++;
    }
    memberStats.workoutsByDate[dateString].activeMinutes = log.minutesCompleted;
    if (log.type === "meditation") {
      memberStats.workoutsByDate[dateString].meditationCount++;
    } else if (log.type === "assessment") {
      memberStats.workoutsByDate[dateString].assessmentCount++;
    } else {
      memberStats.workoutsByDate[dateString].workoutCount++;
    }

    if (log.type === "meditation") {
      memberStats.meditationCount++;
      memberStats.meditationMinutes += log.minutesCompleted;
    } else if (log.type === "assessment") {
      memberStats.assessmentCount++;
      memberStats.assessmentMinutes += log.minutesCompleted;
    } else {
      memberStats.workoutCount++;
      memberStats.activeMinutes += log.minutesCompleted;
    }
    if (!memberStats.workoutSlugCount[log.slug]) {
      memberStats.workoutSlugCount[log.slug] = 0;
    }
    memberStats.workoutSlugCount[log.slug]++;
    if (isNaN(memberStats.workoutTypeCount[log.type])) {
      memberStats.workoutTypeCount.other += 1;
    } else {
      memberStats.workoutTypeCount[log.type] += 1;
    }
  }

  for (const stepLog of stepLogs) {
    const { distanceMeters, stepCount, stepDay } = stepLog || {};
    if (!stepDay) {
      continue;
    }
    const fromDay = format(fromTimestamp, "yyyy-MM-dd");
    const toDay = format(toTimestamp, "yyyy-MM-dd");
    if (
      (!fromTimestamp && !toTimestamp) ||
      fromDay > stepDay ||
      toDay < stepDay
    ) {
      continue;
    }
    memberStats.distanceMeters += distanceMeters;
    memberStats.stepCount += stepCount;
    if (!memberStats.workoutsByDate[stepDay] && (distanceMeters || stepCount)) {
      memberStats.activeDaysCount++;
    }
    memberStats.stepsByDate[stepDay] = {
      stepCount: stepCount || 0,
      distanceMeters: distanceMeters || 0,
    };
  }

  for (const learningLibraryLog of learningLibraryLogs) {
    if (
      fromTimestamp &&
      toTimestamp &&
      (learningLibraryLog.timestampMillis < fromTimestamp ||
        learningLibraryLog.timestampMillis > toTimestamp)
    ) {
      continue;
    }
    const dateString = learningLibraryLog.createdOn.substring(0, 10);
    if (!memberStats.learningLibraryByDate[dateString]) {
      if (
        !memberStats.workoutsByDate[dateString] &&
        !memberStats.stepsByDate[dateString]
      ) {
        memberStats.activeDaysCount++;
      }

      memberStats.learningLibraryByDate[dateString] = {
        learningLibaryViews: 0,
        learningLibraryMinutes: 0,
      };
    }
    memberStats.learningLibraryByDate[dateString].learningLibraryMinutes +=
      learningLibraryLog.minutesCompleted;
    memberStats.learningLibraryByDate[dateString].learningLibaryViews++;
    memberStats.learningLibraryViews++;
    memberStats.learningLibraryMinutes += learningLibraryLog.minutesCompleted;
    if (isNaN(memberStats.workoutTypeCount[learningLibraryLog.type])) {
      memberStats.workoutTypeCount.other += 1;
    } else {
      memberStats.workoutTypeCount[learningLibraryLog.type] += 1;
    }
  }
  return memberStats;
}

//todo: refactor with above?
export function workoutLogsToStatsByUserId(logs: SimpleLog[]) {
  //in // {
  //   uid: string;
  //   createdOn: string;
  //   minutesCompleted: number;
  //   slug: string;
  //   type: WorkoutLogType | LearningLibraryLogType;
  // }
  const dataByUserId: StatsByUserId = {};
  logs.map((log) => {
    const uid = log.uid;
    const dateString = log.createdOn.substring(0, 10);
    if (!dataByUserId[uid]) {
      dataByUserId[uid] = getDefaultMemberStats();
    }
    if (!dataByUserId[uid].workoutsByDate[dateString]) {
      dataByUserId[uid].workoutsByDate[dateString] = {
        activeMinutes: 0,
        workoutCount: 0,
        meditationCount: 0,
        assessmentCount: 0,
      };
      dataByUserId[uid].activeDaysCount++;
    }
    dataByUserId[uid].workoutsByDate[dateString].activeMinutes =
      log.minutesCompleted;
    if (log.type === "meditation") {
      dataByUserId[uid].workoutsByDate[dateString].meditationCount++;
    } else {
      dataByUserId[uid].workoutsByDate[dateString].workoutCount++;
    }

    dataByUserId[uid].activeMinutes += log.minutesCompleted;
    if (log.type === "meditation") {
      dataByUserId[uid].meditationCount++;
    } else {
      dataByUserId[uid].workoutCount++;
    }
    if (!dataByUserId[uid].workoutSlugCount[log.slug]) {
      dataByUserId[uid].workoutSlugCount[log.slug] = 0;
    }
    dataByUserId[uid].workoutSlugCount[log.slug]++;
    if (isNaN(dataByUserId[uid].workoutTypeCount[log.type])) {
      dataByUserId[uid].workoutTypeCount.other += 1;
    } else {
      dataByUserId[uid].workoutTypeCount[log.type] += 1;
    }
  });
  return dataByUserId;
}

export function getTypeFromActivityKey(log: WorkoutLog) {
  if (!log) return null;
  return activityTypesByKey[log?.activityKey]?.category || log.type || null;
}

export function addEditorToOrg(orgId: string, uid: string) {
  const update: Partial<OrganizationDoc> = {
    editors: arrayUnion(uid) as any,
  };
  const orgDoc = doc(firestore, `/organizations/${orgId}`);
  // return firebase.firestore().update(update);
  return updateDoc(orgDoc, update);
}

export function addEditorsToOrg(orgId: string, uids: string[]) {
  const cleanUids = uids?.filter((uid) => uid) || [];
  const update: Partial<OrganizationDoc> = {
    editors: arrayUnion(...cleanUids) as any,
  };
  const orgDoc = doc(firestore, `/organizations/${orgId}`);
  // return firebase.firestore().update(update);
  return updateDoc(orgDoc, update);
}

export function removeEditorsFromOrg(orgId: string, uids: string[]) {
  const update: Partial<OrganizationDoc> = {
    editors: arrayRemove(...uids) as any,
  };
  const orgDoc = doc(firestore, `/organizations/${orgId}`);
  // return firebase.firestore().update(update);
  return updateDoc(orgDoc, update);
}

export async function removeMemberFromOrg(orgId: string, uid: string) {
  const batch = writeBatch(firestore);
  // 1. update org - remove editor from org doc
  const updateOrg: Partial<OrganizationDoc> = {
    editors: arrayRemove(uid) as any,
  };
  const orgRef = doc(firestore, `/organizations/${orgId}`);
  batch.update(orgRef, updateOrg);
  // 2. remove any org groups from user doc
  const userRef = doc(firestore, `/users/${uid}`);
  const orgGroups = await getOrgGroups(orgId);
  for (const removeGroup of orgGroups) {
    const remove = {
      "account.groups": arrayRemove(removeGroup),
    };
    batch.update(userRef, remove);
  }
  const removeOrg = {
    // 3. remove orgId from user doc
    "account.orgId": null,
    // 4. remove premium access from user doc
    "entitlements.allowPremium": null,
  };
  batch.update(userRef, removeOrg);
  return batch.commit();
}

export async function removeEditorFromOrg(orgId: string, uid: string) {
  const update: Partial<OrganizationDoc> = {
    editors: arrayRemove(uid) as any,
  };
  return updateDoc(doc(firestore, `/organizations/${orgId}`), update);
}

export function removeUserFromGroup(uid: string, groupId: string) {
  const remove = {
    "account.groups": arrayRemove(groupId),
  };
  return updateDoc(doc(firestore, `/users/${uid}`), remove);
}

export function addUserToGroup(uid: string, groupId: string) {
  const add = {
    "account.groups": arrayUnion(groupId),
  };
  return updateDoc(doc(firestore, `/users/${uid}`), add);
}

export async function createGroup(orgId: string, groupName: string) {
  const uid = getLoggedInUserId();
  const ref = doc(collection(firestore, `/groups/`));
  const groupDoc: Group = {
    id: ref.id,
    name: groupName,
    orgId,
    createdByUid: uid,
    ownerUids: [],
  };
  await setDoc(ref, groupDoc);
  return groupDoc;
}

export function fireStoreUpdateAssignmentMember(
  assignmentMember: AssignedMember,
  assignmentId: string,
) {
  if (assignmentMember && assignmentId) {
    assignmentMember.assignmentId = assignmentId;
    const docRef = doc(
      firestore,
      `/assignments/${assignmentId}/assignmentMembers/${assignmentMember.uid}`,
    );
    return setDoc(docRef, assignmentMember, { merge: true });
  } else {
    return Promise.reject("assignmentMember or assignmentId is null");
  }
}

export function fireStoreDeleteAssignmentMember(
  assignmentMemberId: string,
  assignmentId: string,
) {
  const docRef = doc(
    firestore,
    `/assignments/${assignmentId}/assignmentMembers/${assignmentMemberId}`,
  );
  return deleteDoc(docRef);
}

export async function setAssignmentMembers(
  assignmentId: string,
  members: AssignedMember[],
) {
  if (members && members.length > 0 && assignmentId) {
    for (const member of members) {
      await fireStoreUpdateAssignmentMember(member, assignmentId);
    }
  }
}

export async function firestoreCreateAssignment(assignment: Assignment) {
  const assignmentMemberData: AssignedMember[] = assignment?.assignedMembers;
  const docRef = doc(collection(firestore, `assignments`));
  const assignmentDoc: Assignment = {
    assignmentId: docRef.id,
    ...assignment,
  };
  setAssignmentMembers(assignmentDoc.assignmentId, assignmentMemberData);
  return setDoc(docRef, assignmentDoc);
}

export async function firestoreUpdateAssignment(
  assignment: Assignment,
): Promise<void> {
  if (assignment.assignedMembers) {
    const assignmentMemberData = assignment.assignedMembers;
    delete assignment.assignedMembers;
    setAssignmentMembers(assignment.assignmentId, assignmentMemberData);
  }
  return setDoc(
    doc(firestore, `/assignments/${assignment.assignmentId}`),
    assignment,
    { merge: true },
  );
}

export async function getGroup(groupId: string) {
  const groupDoc = await getDoc(doc(firestore, `/groups/${groupId}`));
  return groupDoc.data() as Group;
}

export function updateGroupName(groupId: string, groupName: string) {
  const update: Partial<Group> = {
    name: groupName,
  };
  return updateDoc(doc(firestore, `/groups/${groupId}`), update);
}

export function updateGroupOwners(groupId: string, ownerUids: string[]) {
  const update: Partial<Group> = {
    ownerUids,
  };
  return updateDoc(doc(firestore, `/groups/${groupId}`), update);
}

export function deleteGroup(groupId: string) {
  return deleteDoc(doc(firestore, `/groups/${groupId}`));
}

export async function getGroupMembers(groupId: string) {
  const query = await getDocs(
    collection(firestore, `/groups/${groupId}/members`),
  );
  const members = query.docs.map((doc) => doc.data() as GroupMember);
  return members;
}

export async function subscribeToOrgAssignments(
  orgId: string,
  callback: (assignments: Assignment[]) => void,
) {
  const q = query(
    collection(firestore, "/assignments"),
    where("orgId", "==", orgId),
  );
  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.docs.map((doc) => doc.data() as Assignment));
  });
}

export async function subscribeToAssignment(assignmentId: string) {
  const assignmentDoc = await getDoc(
    doc(firestore, `/assignments/${assignmentId}`),
  );
  return assignmentDoc.data() as Assignment;
}

export async function subscribeToSelectedAssignmentMembers(
  assignmentId: string,
  callback: (assignments: AssignedMember[]) => void,
) {
  const q = collection(
    firestore,
    `/assignments/${assignmentId}/assignmentMembers`,
  );
  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.docs.map((doc) => doc.data() as AssignedMember));
  });
}

export async function subscribeToMemberAssignments(
  uid: string,
  callback: (assignments: AssignedMember[]) => void,
) {
  const q = query(
    collectionGroup(firestore, "assignmentMembers"),
    where("uid", "==", uid),
  );
  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.docs.map((doc) => doc.data() as AssignedMember));
  });
}

export async function subscribeToMemberAssignmentsUids(
  uid: string,
  callback: (userAssignmentData: Assignment[]) => void,
) {
  const q = query(
    collectionGroup(firestore, "assignmentMembers"),
    where("uid", "==", uid),
  );
  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.docs.map((doc) => doc.data() as Assignment));
  });
}

export async function getOrgGroups(orgId: string) {
  const q = query(
    collection(firestore, "/groups"),
    where("orgId", "==", orgId),
  );
  const queryResults = await getDocs(q);
  const groups = queryResults.docs.map((doc) => doc.data() as Group);
  return groups;
}

export async function getOrgGroupNameMapFromCache(orgId: string) {
  if (portalGroupsState.orgGroups?.length) {
    return portalGroupsState.groupNameMap;
  }
  const groups = await getOrgGroups(orgId);
  portalGroupsState.orgGroups = [...groups];
  //hopefully this works..
  return portalGroupsState.groupNameMap;
}

export async function getUserWorkoutLogsBetweenTimestamps(
  uid: string,
  fromTimestamp: number,
  toTimestamp: number,
) {
  const q = query(
    collection(firestore, `/users/${uid}/workoutLogs`),
    where("timestampMillis", ">=", fromTimestamp),
    where("timestampMillis", "<=", toTimestamp),
    orderBy("timestampMillis", "desc"),
  );
  const queryResult = await getDocs(q);
  return queryResult.docs.map((doc) => doc.data() as WorkoutLog);
}

//
export async function getUserLearningLibraryLogsBetweenTimestamps(
  uid: string,
  fromTimestamp: number,
  toTimestamp: number,
) {
  const q = query(
    collection(firestore, `/users/${uid}/learningLibraryLogs`),
    where("timestampMillis", ">=", fromTimestamp),
    where("timestampMillis", "<=", toTimestamp),
    orderBy("timestampMillis", "desc"),
  );
  const queryResult = await getDocs(q);
  return queryResult.docs.map((doc) => doc.data() as LearningLibraryLog);
}

export async function saveInvite(invite: InviteDocument) {
  let ref: DocumentReference;
  const inviteDoc: InviteDocument = {
    ...invite,
    createdOn: new Date().getTime(),
  };
  if (inviteDoc.temporaryPasswordInvite) {
    return saveInvites([inviteDoc]);
  }
  if (inviteDoc.id) {
    ref = doc(firestore, `/invites/${inviteDoc.id}`);
  } else {
    ref = doc(collection(firestore, `/invites/`));
    inviteDoc.id = ref.id;
  }
  const cleanInvite = removeUndefinedAndNaNRecursive(inviteDoc);
  return setDoc(ref, cleanInvite, { merge: true });
}

export const saveInvites = (
  temporaryPasswordInvites: TemporaryPasswordInviteDocument[],
) => httpsCallable(firebaseFunctions, "saveInvites")(temporaryPasswordInvites);

export async function getOrgInvitesAsMembers(orgId: string) {
  const invites = await getOrgInvites(orgId);
  return convertInvitesToMembers(invites);
}

export function convertInvitesToMembers(invites: InviteDocument[]) {
  return invites.map((invite) => {
    const member: GroupMemberInvite = {
      firstName: invite.firstName,
      lastName: invite.lastName,
      groups: invite.groups,
      uid: null,
      inviteId: invite.id,
      email: invite.email,
    };
    return member;
  });
}

export async function getGroupInvitesAsMembers(groupId: string) {
  const q = query(
    collection(firestore, `invites`),
    where("groups", "array-contains", groupId),
  );
  const queryResults = await getDocs(q);
  const members = queryResults.docs.map((doc) => {
    const invite = doc.data() as InviteDocument;
    const member: GroupMember = {
      firstName: invite.firstName,
      lastName: invite.lastName,
      groups: invite.groups,
      uid: null,
    };
    return member;
  });
  return members;
}

export function deleteInvite(inviteId: string) {
  return deleteDoc(doc(firestore, `invites/${inviteId}`));
}

function getDefaultMemberStats() {
  const data: GroupMemberStats = {
    activeDaysCount: 0,
    activeMinutes: 0,
    workoutCount: 0,
    meditationCount: 0,
    assessmentCount: 0,
    assessmentMinutes: 0,
    workoutSlugCount: {},
    workoutTypeCount: {
      strength: 0,
      yoga: 0,
      cardio: 0,
      stretching: 0,
      other: 0,
      meditation: 0,
      nutrition: 0,
      assessment: 0,
    },
    stepCount: 0,
    distanceMeters: 0,
    workoutsByDate: {},
    stepsByDate: {},
    learningLibraryByDate: {},
    learningLibraryMinutes: 0,
    learningLibraryViews: 0,
    meditationMinutes: 0,
  };
  return data;
}

export function updateOrg(orgId: string, update: Partial<OrganizationDoc>) {
  if (!orgId) return;
  const ref = doc(firestore, `/organizations/${orgId}`);
  const updateClean = removeUndefinedAndNaNRecursive(update);
  return setDoc(ref, updateClean, { merge: true });
}

export function listenToOrg(
  orgId: string,
  callback: (org: OrganizationDoc) => void,
) {
  const q = doc(firestore, `organizations/${orgId}`);

  return onSnapshot(q, (orgDoc) => {
    callback({ ...orgDoc.data(), id: orgDoc.id } as OrganizationDoc);
  });
}

export function listenToOrgMembers(
  orgId: string,
  callback: (orgMembers: GroupMember[], modified?: GroupMember[]) => void,
) {
  const q = query(collection(firestore, `organizations/${orgId}/members`));
  return onSnapshot(q, (querySnapshot) => {
    const modified = querySnapshot
      .docChanges()
      .map((change) => {
        // only need to check on modified, we will always do the update if adding or subtracting
        if (change.type === "modified") {
          return change.doc.data() as GroupMember;
        }
      })
      .filter((member) => member);
    const groupMembers = querySnapshot.docs.map(
      (doc) => doc.data() as GroupMember,
    );
    callback(groupMembers, modified);
  });
}

export function listenToOrgAdmins(
  orgId: string,
  callback: (orgMembers: GroupMember[], modified?: GroupMember[]) => void,
) {
  const q = query(
    collection(firestore, `orgAdmins`),
    where("orgIds", "array-contains", orgId),
  );
  return onSnapshot(q, (querySnapshot) => {
    const modified = querySnapshot
      .docChanges()
      .map((change) => {
        // only need to check on modified, we will always do the update if adding or subtracting
        if (change.type === "modified") {
          return change.doc.data() as GroupMember;
        }
      })
      .filter((member) => member);
    const groupMembers = querySnapshot.docs.map(
      (doc) => ({ ...doc.data(), uid: doc.id }) as GroupMember,
    );
    callback(groupMembers, modified);
  });
}

export function listenToOrgInvites(
  orgId: string,
  callback: (orgInvites: InviteDocument[]) => void,
) {
  const q = query(
    collection(firestore, `invites`),
    where("orgId", "==", orgId),
  );
  return onSnapshot(q, (querySnapshot) => {
    callback(querySnapshot.docs.map((doc) => doc.data() as InviteDocument));
  });
}

export async function getOrgMemberUidMapFromOrgIds(
  orgIds: string[],
): Promise<{ [uid: string]: GroupMember }> {
  const membersPromise = orgIds.map((orgId) => getOrgMembers(orgId));
  const memberResults = await Promise.all(membersPromise);
  const allMembers = memberResults?.flat() || [];
  return [...allMembers].reduce((acc, member) => {
    acc[member.uid] = member;
    return acc;
  }, {});
}
